import validate from 'validate.js/validate';

export interface IRequiredFieldConfig {
  message: string;
}

export interface IRequiredFieldsConstraint {
  fields: Record<string, IRequiredFieldConfig>;
  unless?: (attributes: Record<string, any>) => boolean;
}

/**
 * Custom validate.js validator that checks multiple fields within a nested object for presence.
 *
 * Unlike `presenceUnless` which validates a single field conditionally, this validator checks
 * that several fields inside a nested object (e.g., `meta_settings`) are all present, gated
 * by a single `unless` condition.
 *
 * @param value - The nested object to validate (e.g., the value of `meta_settings`)
 * @param options - Constraint config specifying which fields to require and an optional skip condition
 * @param key - The attribute name being validated
 * @param attributes - The full model attributes, passed to the `unless` callback
 * @returns Array of error messages for missing fields, or `undefined` if all are present
 */
function requiredFields(
  value: Record<string, any> | undefined,
  options: IRequiredFieldsConstraint,
  key: string,
  attributes: Record<string, any>,
): string[] | undefined {
  const { fields, unless } = options;

  if (unless?.(attributes)) {
    return undefined;
  }

  const errors: string[] = [];

  for (const [field, { message }] of Object.entries(fields)) {
    const fieldValue = value?.[field];
    if (validate.isEmpty(fieldValue)) {
      errors.push(message);
    }
  }

  return errors.length > 0 ? errors : undefined;
}

export default requiredFields;
